import React, { useEffect, useState } from 'react'
import CartService from '../service/CartService';
import '../css/checkout.css'
import { MdAdd, MdDeleteOutline, MdRemove } from 'react-icons/md';
import { HiOutlineShoppingBag, HiOutlineEmojiSad, HiOutlineClock, HiOutlineLockClosed, HiOutlineEyeOff } from 'react-icons/hi';
import { Link, useHistory } from 'react-router-dom'
import { formatRupee } from '../components/Price';
import OrderSummary from '../components/OrderSummary';
import Skeleton from '../components/Skeleton';
import EmptyState from '../components/EmptyState';
import CouponBox from '../components/CouponBox';
import { cartCoupon } from '../util/coupons';
import { notifyCartUpdated } from '../util/cartEvents';

const MAX_QUANTITY = 5

const CartView = () => {

    const history = useHistory();

    const [cartItems, setCartItems] = useState([])
    const [isLoading, setIsLoading] = useState(true)
    const [loadError, setLoadError] = useState(false)
    const [error1, setError1] = useState('')
    const [total, setTotal] = useState(null)
    const [deliveryCharges, setDeliveryCharges] = useState(0)
    const [finalPrice, setFinalPrice] = useState(null)
    const [isCartInProcess, setIsCartInProcess] = useState(false)
    const [hasUnavailable, setHasUnavailable] = useState(false) // a product was hidden after it was added
    const [busyCartId, setBusyCartId] = useState(null)
    const [coupon, setCoupon] = useState(null) // cartCoupon(): applied coupon, nudge, error

    const updateQuantity = (cartId, quantity) => {

        if (quantity < 1) {
            deleteCartItem(cartId)
            return;
        }

        if (quantity > MAX_QUANTITY) {
            setError1(`You can add up to ${MAX_QUANTITY} of each item.`)
            return
        }

        updateCartItem(cartId, quantity)
    }

    const forwardToConfirmationPage = () => {
        if (hasUnavailable) return
        history.push({
            pathname: '/payment'
        })
    }

    const updateCartItem = (cartId, quantity) => {
        const formData = new FormData();
        formData.append("quantity", quantity);
        formData.append("cartId", cartId);

        setBusyCartId(cartId)
        CartService.updateCart(formData).then(response => {
            if (response.status === 200) {
                setError1('')
                fetchCart()
            }
        }).catch(error => {
            setBusyCartId(null)
            if (error?.response?.status === 400) {
                setError1("Sorry, the requested quantity is not available.")
            } else {
                setError1("Could not update your cart. Please try again.")
            }
        });
    }

    const deleteCartItem = (cartId) => {
        setBusyCartId(cartId)
        CartService.deleteCart(cartId).then(response => {
            if (response.status === 200) {
                setError1('')
                fetchCart()
                notifyCartUpdated()
            }
        }).catch(error => {
            setBusyCartId(null)
            setError1("Could not remove this item. Please try again.")
        });
    }

    const showCart = (data) => {
        setCartItems(data.cartListView)
        setTotal(data.cartItemTotal)
        setDeliveryCharges(data.deliveryCharges)
        setFinalPrice(data.finalPrice)
        setIsCartInProcess(data.isCartInProcess)
        setHasUnavailable(!!data.hasUnavailable)
        setCoupon(cartCoupon(data))
    }

    const fetchCart = () => {
        CartService.getCartItems().then(response => {
            if (response.status) {
                showCart(response.data.data)
                setLoadError(false)
                setIsLoading(false)
                setBusyCartId(null)
            }
        }).catch(error => {
            console.log(error)
            if (error?.response?.status === 401) {
                history.push({ pathname: '/login', state: { id: '/cart' } })
                return
            }
            setLoadError(true)
            setIsLoading(false)
            setBusyCartId(null)
        });
    }

    useEffect(() => {
        fetchCart()
    }, []);

    const retry = () => {
        setIsLoading(true)
        fetchCart()
    }

    if (isLoading) {
        return (
            <main className='mv-page mv-cart' aria-busy='true'>
                <Skeleton width='160px' height={32} />
                <div className='mv-cart-layout'>
                    <div className='mv-cart-items'>
                        {[0, 1].map(i => <Skeleton key={i} height={128} radius='14px' />)}
                    </div>
                    <Skeleton height={220} radius='14px' />
                </div>
            </main>
        )
    }

    if (loadError) {
        return (
            <main className='mv-page'>
                <EmptyState icon={<HiOutlineEmojiSad />} title='We could not load your cart'
                    text='Please check your connection and try again.' actionLabel='Try again' onAction={retry} />
            </main>
        )
    }

    if (isCartInProcess) {
        return (
            <main className='mv-page'>
                <EmptyState icon={<HiOutlineClock />} title='Your order is being processed'
                    text='Once it is completed, you will be able to add new items to your cart.'
                    actionLabel='View my orders' onAction={() => history.push('/orders')} />
            </main>
        )
    }

    if (cartItems == null || cartItems.length === 0) {
        return (
            <main className='mv-page'>
                <EmptyState icon={<HiOutlineShoppingBag />} title='Your cart is empty'
                    text='Looks like you have not added anything yet. Explore our latest styles for your little ones.'
                    actionLabel='Start shopping' onAction={() => history.push('/')} />
            </main>
        )
    }

    const isLineAvailable = (e) => e.isAvailable !== false
    const itemCount = cartItems.filter(isLineAvailable).reduce((sum, e) => sum + Number(e.quantity || 0), 0)
    const blockedNote = 'Remove the items that are no longer available to continue.'

    return (
        <main className='mv-page mv-cart'>
            <h1 className='mv-list-title'>My cart</h1>

            <div className='mv-cart-layout'>
                <section className='mv-cart-items' aria-label='Cart items'>
                    {error1 && <div className='mv-alert mv-alert-error' role='alert'>{error1}</div>}
                    {hasUnavailable && <div className='mv-alert mv-alert-error' role='alert'>{blockedNote}</div>}

                    {cartItems.map(e => {
                        const busy = busyCartId === e.cartId
                        if (!isLineAvailable(e)) {
                            return (
                                <article className={busy ? 'mv-cart-item mv-cart-item-off busy' : 'mv-cart-item mv-cart-item-off'} key={e.cartId}>
                                    <span className='mv-cart-thumb'>
                                        <img src={e.image} alt={e.itemName} loading='lazy' />
                                    </span>
                                    <div className='mv-cart-info'>
                                        <span className='mv-cart-name'>{e.itemName}</span>
                                        {e.size && <span className='mv-cart-size'>Size: {e.size}</span>}
                                        <span className='mv-chip mv-chip-muted mv-cart-off-chip'><HiOutlineEyeOff aria-hidden='true' /> No longer available</span>
                                        <div className='mv-cart-actions'>
                                            <button type='button' className='mv-btn mv-btn-secondary mv-btn-sm' disabled={busy}
                                                onClick={() => deleteCartItem(e.cartId)} aria-label={`Remove ${e.itemName}`}>
                                                <MdDeleteOutline aria-hidden='true' /> {busy ? 'Removing…' : 'Remove'}
                                            </button>
                                        </div>
                                    </div>
                                </article>
                            )
                        }
                        return (
                            <article className={busy ? 'mv-cart-item busy' : 'mv-cart-item'} key={e.cartId}>
                                <Link to={'/shop/items/view/' + e.itemId} className='mv-cart-thumb'>
                                    <img src={e.image} alt={e.itemName} loading='lazy' />
                                </Link>
                                <div className='mv-cart-info'>
                                    <Link to={'/shop/items/view/' + e.itemId} className='mv-cart-name'>{e.itemName}</Link>
                                    {e.size && <span className='mv-cart-size'>Size: {e.size}</span>}
                                    <span className='mv-cart-unit'>{formatRupee(e.price)} each</span>

                                    <div className='mv-cart-actions'>
                                        <div className='mv-stepper' role='group' aria-label={`Quantity for ${e.itemName}`}>
                                            <button type='button' className='mv-stepper-btn' disabled={busy}
                                                onClick={() => updateQuantity(e.cartId, e.quantity - 1)}
                                                aria-label={e.quantity === 1 ? 'Remove item' : 'Decrease quantity'}>
                                                {e.quantity === 1 ? <MdDeleteOutline /> : <MdRemove />}
                                            </button>
                                            <span className='mv-stepper-value' aria-live='polite'>{e.quantity}</span>
                                            <button type='button' className='mv-stepper-btn' disabled={busy || e.quantity >= MAX_QUANTITY}
                                                onClick={() => updateQuantity(e.cartId, e.quantity + 1)} aria-label='Increase quantity'>
                                                <MdAdd />
                                            </button>
                                        </div>
                                        <span className='mv-cart-line-total'>{formatRupee(Number(e.price) * e.quantity)}</span>
                                    </div>
                                </div>
                            </article>
                        )
                    })}
                </section>

                <aside className='mv-cart-aside'>
                    <OrderSummary subtotal={total} delivery={deliveryCharges} total={finalPrice} itemCount={itemCount}
                        discount={coupon && coupon.code ? coupon.discount : 0} couponCode={coupon && coupon.code}
                        freeDelivery={!!(coupon && coupon.freeDelivery)} saving={coupon && coupon.code ? coupon.saving : 0}
                        coupon={<CouponBox coupon={coupon} onCart={showCart} />}>
                        <button type='button' className='mv-btn mv-btn-primary mv-btn-block mv-summary-cta' onClick={forwardToConfirmationPage}
                            disabled={hasUnavailable} title={hasUnavailable ? blockedNote : undefined}>
                            <HiOutlineLockClosed /> Checkout
                        </button>
                    </OrderSummary>
                    <p className='mv-secure-note'>Safe and secure payments · Easy help on WhatsApp</p>
                </aside>
            </div>

            <div className='mv-checkout-bar'>
                <div>
                    <span className='mv-checkout-bar-label'>Total</span>
                    <span className='mv-checkout-bar-total'>{formatRupee(finalPrice)}</span>
                    {coupon && coupon.code && coupon.saving > 0 && <span className='mv-checkout-bar-save'>You save {formatRupee(coupon.saving)}</span>}
                </div>
                <button type='button' className='mv-btn mv-btn-primary' onClick={forwardToConfirmationPage}
                    disabled={hasUnavailable} title={hasUnavailable ? blockedNote : undefined}>
                    <HiOutlineLockClosed /> Checkout
                </button>
            </div>
        </main>
    )
}

export default CartView
